import {useTrackFeature} from '@aboutyou/tracking';
import {useEffect, useRef} from 'react';
import {
  ContentBlockPromotionTrackingInterface,
  useContentBlockPromotionTracking,
} from './useContentBlockPromotionTracking';
import {useContentBlockPromotionTrackingImpression} from './useContentBlockPromotionTrackingImpression';
/**
 * Returns a ref object for the video tile which will be tracked and a function to trigger a promotion tracking when the video ends
 * @param promotionName if 1st CTA get text || if 2nd CTA get text || if subline get text || if headline get text || ""
check subline if available, else
check headline if available, else
empty string
 * @returns 
 */
export const useTrackVideoPromotion = (
  props: Omit<
    ContentBlockPromotionTrackingInterface,
    'actionType' | 'contentBlockSource'
  >,
) => {
  const isVideoEndTracked = useRef(false);
  const canTrackPlayevent = useRef(true);
  const trackFeature = useTrackFeature();
  const promotionTrackingClick = useContentBlockPromotionTracking();
  const contentBlockSource = 'video';

  const {elementRef} =
    useContentBlockPromotionTrackingImpression<HTMLVideoElement>({
      ...props,
      contentBlockSource,
    });

  // The next useEffect is handling 2 eventListeners for the video tile on this custom hook, it's taking many lines mostly because of the 2 function handlers
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const refEl = elementRef.current;
    const handlePlayEvent = () => {
      if (refEl && canTrackPlayevent.current) {
        promotionTrackingClick({
          ...props,
          actionType: 'click',
          contentBlockSource,
        });
      }
    };

    const handlePauseEvent = () => {
      if (refEl) {
        trackFeature({
          eventType: 'click',
          label: `video|${String(props.blockId ?? '')}|${refEl.currentTime}`,
        });
      }
    };

    let timeout: NodeJS.Timeout;

    // hack to avoid tracking video on loop since the loop is controlled by is and when the video ends the play event is fired making this tracking function to be called when the video "loops"
    const videoEndHandler = () => {
      canTrackPlayevent.current = false;

      timeout = setTimeout(() => {
        canTrackPlayevent.current = true;
      }, 500);
    };

    refEl?.addEventListener('play', handlePlayEvent);
    refEl?.addEventListener('pause', handlePauseEvent);
    refEl?.addEventListener('ended', videoEndHandler);

    return () => {
      refEl?.removeEventListener('play', handlePlayEvent);
      refEl?.removeEventListener('pause', handlePauseEvent);
      refEl?.removeEventListener('ended', videoEndHandler);
      clearTimeout(timeout);
    };
  }, [elementRef.current]);

  const trackFeatureOnVideoEnds = () => {
    if (elementRef.current && !isVideoEndTracked.current) {
      trackFeature({
        eventType: 'close',
        label: `video|${String(props.blockId ?? '')}|${
          elementRef.current.currentTime
        }`,
      });

      isVideoEndTracked.current = true;
    }
  };

  return {
    elementRef,
    trackFeatureOnVideoEnds,
  };
};
